import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconPencil, IconTrash, IconSend } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import api from '../api/api';

export default function BoardViewPage({ me, showToast }) {
  const { boardId, postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 수정 모드
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');

  // React 18 Strict Mode에서 useEffect 이중 실행 방지
  const loadedRef = useRef(null);

  useEffect(() => {
    if (loadedRef.current !== postId) {
      loadedRef.current = postId;
      loadPost();
    }
  }, [postId]);

  const loadPost = async () => {
    setLoading(true);
    try {
      const [postRes, commentRes] = await Promise.all([
        api.get(`/posts/${postId}`),
        api.get(`/comments?postId=${postId}`),
      ]);
      setPost(postRes.data);
      setComments(commentRes.data);
      setEditTitle(postRes.data.title);
      setEditContent(postRes.data.content || '');
    } catch {
      showToast('게시글을 불러오는데 실패했습니다.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/comments', { postId: Number(postId), content: commentText.trim() });
      setCommentText('');
      const res = await api.get(`/comments?postId=${postId}`);
      setComments(res.data);
      showToast('댓글이 등록되었습니다.', 'success');
    } catch {
      showToast('댓글 등록에 실패했습니다.', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('게시글을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/posts/${postId}`);
      showToast('게시글이 삭제되었습니다.', 'success');
      navigate(`/board/${boardId}`);
    } catch {
      showToast('삭제에 실패했습니다.', 'danger');
    }
  };

  const handleUpdate = async () => {
    if (!editTitle.trim()) { showToast('제목을 입력해주세요.', 'danger'); return; }
    try {
      await api.put(`/posts/${postId}`, { title: editTitle.trim(), content: editContent });
      showToast('게시글이 수정되었습니다.', 'success');
      setEditing(false);
      loadPost();
    } catch {
      showToast('수정에 실패했습니다.', 'danger');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/comments/${commentId}`);
      const res = await api.get(`/comments?postId=${postId}`);
      setComments(res.data);
      showToast('댓글이 삭제되었습니다.', 'success');
    } catch {
      showToast('댓글 삭제에 실패했습니다.', 'danger');
    }
  };

  const fmt = (dt) => dt ? new Date(dt).toLocaleString('ko-KR') : '-';
  const isAuthor = post && me && post.authorId === me.id;

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  }

  if (!post) {
    return <div className="text-center py-5 text-muted">게시글을 찾을 수 없습니다.</div>;
  }

  return (
    <div>
      {/* 상단 네비 */}
      <button className="btn btn-ghost-secondary btn-sm mb-3 d-flex align-items-center gap-1"
        onClick={() => navigate(`/board/${boardId}`)}>
        <IconArrowLeft size={15} /> 목록으로
      </button>

      <div className="card mb-3">
        {editing ? (
          <div className="card-body">
            <div className="mb-3">
              <input type="text" className="form-control form-control-sm" value={editTitle} onChange={e => setEditTitle(e.target.value)} />
            </div>
            <div className="mb-3">
              <textarea className="form-control form-control-sm" rows={10} value={editContent} onChange={e => setEditContent(e.target.value)} />
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={handleUpdate}>저장</button>
              <button className="btn btn-ghost-secondary btn-sm" onClick={() => setEditing(false)}>취소</button>
            </div>
          </div>
        ) : (
          <>
            <div className="card-header">
              <div className="d-flex align-items-start justify-content-between w-100">
                <div>
                  <h3 className="card-title mb-1">{post.title}</h3>
                  <div className="text-muted small">
                    {post.authorName} &middot; {fmt(post.createdAt)} &middot; 조회 {post.viewCount}
                  </div>
                </div>
                {isAuthor && (
                  <div className="d-flex gap-1">
                    <button className="btn btn-ghost-primary btn-sm" onClick={() => setEditing(true)}>
                      <IconPencil size={14} />
                    </button>
                    <button className="btn btn-ghost-danger btn-sm" onClick={handleDelete}>
                      <IconTrash size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="card-body">
              <div className="post-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '') }} />

              {/* 첨부파일 */}
              {post.attachments && post.attachments.length > 0 && (
                <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--tblr-border-color)' }}>
                  <div className="text-muted small mb-2">첨부파일</div>
                  {post.attachments.map(f => (
                    <a key={f.id} href={`${api.defaults.baseURL}/files/${f.id}`} className="d-block small" download>
                      {f.originalName} ({(f.fileSize / 1024).toFixed(1)} KB)
                    </a>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 댓글 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title mb-0">댓글 ({comments.length})</h3>
        </div>
        <div className="card-body p-0">
          {comments.length === 0 ? (
            <div className="text-center text-muted py-4">댓글이 없습니다.</div>
          ) : (
            <div className="list-group list-group-flush">
              {comments.map(c => (
                <div key={c.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold small">{c.authorName}
                        <span className="text-muted fw-normal ms-2">{fmt(c.createdAt)}</span>
                      </div>
                      <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{c.content}</div>
                    </div>
                    {me && c.authorId === me.id && (
                      <button className="btn btn-ghost-danger btn-sm" onClick={() => handleDeleteComment(c.id)}>
                        <IconTrash size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-footer">
          <div className="d-flex gap-2">
            <textarea className="form-control form-control-sm" rows={2} placeholder="댓글을 입력하세요"
              value={commentText} onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); } }} />
            <button className="btn btn-sm btn-primary d-flex align-items-center" onClick={handleComment} disabled={submitting}>
              <IconSend size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
